/**
 * screens/ObRulesScreen.tsx — Outbound Rules (Controls tier).
 *
 * Faithful to the prototype:
 *   - info banner (Controls tier; regulatory class/priority/ceilings are
 *     Softknack-owned and not editable here)
 *   - Outgoing / Campaigns sub-tabs
 *   - Outgoing: LOCKED quiet-hours guardrail (quiet from/until, country, tz —
 *     all read-only with a Locked badge) · daily frequency cap (per customer)
 *     · per-org concurrency + default lead · retry matrix per outcome
 *     (delay + max + terminal toggle)
 *   - Campaigns: send rate / concurrency / daily cap / send window +
 *     DND-dedupe & retry-once toggles
 *
 * Wired to /v1/outbound/settings (quiet hours read-only from regulatory_profile).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, Pressable, TextInput, Switch } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { Card, Chip, Button, ScreenBody } from '../components/voiceUi';
import { getOutboundSettings, updateOutboundSettings, updateReasonWindows, updateRetryRules, listOutboundReasons } from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

const RETRY_OUTCOMES: Array<[string, string]> = [
  ['busy', 'Busy'], ['no_answer', 'No answer'], ['network_failure', 'Network failure'],
  ['voicemail', 'Voicemail'], ['declined_mid_call', 'Declined mid-call'], ['stop_request', 'Stop request'],
];

type WinRow = { reason_code: string; window_start: string; window_end: string; lead_minutes: string; regulatory_class?: string; carve_out?: boolean };
type RetryRow = { retry_delay_minutes: string; max_retries: string; terminal: boolean };

export default function ObRulesScreen() {
  const { theme } = useTheme(); const c = theme.colors;
  const { role, orgId } = useConsole();
  const canWrite = role !== 'viewer';

  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'outgoing' | 'campaigns'>('outgoing');
  const [cap, setCap] = useState(''); const [conc, setConc] = useState(''); const [lead, setLead] = useState('');
  const [obEnabled, setObEnabled] = useState(true); const [togglingOb, setTogglingOb] = useState(false);
  const [winRows, setWinRows] = useState<WinRow[]>([]);
  const [retryRows, setRetryRows] = useState<Record<string, RetryRow>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: d }, reasonsRes] = await Promise.all([getOutboundSettings(), listOutboundReasons()]);
    const reasons = reasonsRes.data;
    setData(d);
    if (d?.settings) { setCap(String(d.settings.dailyCallCap)); setConc(String(d.settings.maxConcurrency)); setLead(String(d.settings.defaultLeadMinutes)); setObEnabled(d.settings.outboundEnabled !== false); }

    // Preferred windows: one editable row per active reason, prefilled from any
    // saved window, else the org default (10:00–19:00, lead = org default).
    const savedWins: Record<string, any> = {};
    for (const w of (d?.reasonWindows ?? [])) savedWins[w.reasonCode] = w;
    const defLead = String(d?.settings?.defaultLeadMinutes ?? 30);
    setWinRows((reasons ?? []).map((r: any) => {
      const w = savedWins[r.reasonCode];
      return {
        reason_code: r.reasonCode,
        window_start: w?.windowStart ?? '10:00', window_end: w?.windowEnd ?? '19:00',
        lead_minutes: String(w?.leadMinutes ?? defLead),
        regulatory_class: r.regulatoryClass, carve_out: r.ignoresFrequencyCap,
      };
    }));

    // Retry matrix: one editable row per outcome, prefilled from any saved rule
    // (rules are stored per reason but identical across reasons — read the first).
    const savedRetry: Record<string, any> = {};
    for (const rr of (d?.retryRules ?? [])) if (!savedRetry[rr.outcome]) savedRetry[rr.outcome] = rr;
    const rmap: Record<string, RetryRow> = {};
    for (const [code] of RETRY_OUTCOMES) {
      const rr = savedRetry[code];
      const terminal = !rr || rr.retryDelayMinutes == null;
      rmap[code] = { retry_delay_minutes: terminal ? '' : String(rr.retryDelayMinutes), max_retries: String(rr?.maxRetries ?? 0), terminal };
    }
    setRetryRows(rmap);
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load, orgId]);

  const setRetry = (code: string, patch: Partial<RetryRow>) =>
    setRetryRows((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));
  const setWin = (i: number, patch: Partial<WinRow>) =>
    setWinRows((prev) => prev.map((w, j) => (j === i ? { ...w, ...patch } : w)));

  // Master switch saves immediately (independent of the Save button) — flipping
  // it off is a safety action the owner expects to take effect now.
  const toggleOutbound = async (next: boolean) => {
    setObEnabled(next); setTogglingOb(true);
    try { await updateOutboundSettings({ outbound_enabled: next }); showSuccess(null, next ? 'Outbound calling on' : 'Outbound calling off'); }
    catch (e) { setObEnabled(!next); handleApiError(e, 'Could not update outbound switch'); }
    finally { setTogglingOb(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await updateOutboundSettings({ daily_call_cap: Number(cap), max_concurrency: Number(conc), default_lead_minutes: Number(lead) });
      await updateReasonWindows(winRows.map((w) => ({
        reason_code: w.reason_code, window_start: w.window_start, window_end: w.window_end, lead_minutes: Number(w.lead_minutes) || 0,
      })));
      await updateRetryRules(RETRY_OUTCOMES.map(([code]) => {
        const r = retryRows[code];
        return { outcome: code, retry_delay_minutes: r.terminal ? null : (Number(r.retry_delay_minutes) || 0), max_retries: r.terminal ? 0 : (Number(r.max_retries) || 0) };
      }));
      showSuccess(null, 'Outbound rules saved'); void load();
    }
    catch (e) { handleApiError(e, 'Save failed'); } finally { setBusy(false); }
  };

  if (loading) return <ScreenBody><ActivityIndicator color={c.ink3} /></ScreenBody>;
  const reg = data?.regulatory;
  const ceiling = data?.settings?.dailyCallCapCeiling ?? 3;
  const camp = data?.campaignSettings;

  return (
    <ScreenBody maxWidth={1000}>
      {/* info banner */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderWidth: 1, borderColor: c.line, borderRadius: 10, backgroundColor: c.paper2, marginBottom: 20 }}>
        <Text style={{ fontSize: 12.5, color: c.ink2, flex: 1 }}>
          <Text style={{ fontWeight: '700' }}>Controls tier.</Text> These bound the engine — SK Voice still can't propose a time outside them. Regulatory class, priority and ceilings are Softknack-owned (Setup tier) and not editable here.
        </Text>
      </View>

      {/* sub-tabs */}
      <View style={{ flexDirection: 'row', gap: 4, padding: 4, backgroundColor: c.paper3 ?? c.paper2, borderRadius: 9, marginBottom: 20, alignSelf: 'flex-start' }}>
        {(['outgoing', 'campaigns'] as const).map((t) => (
          <Pressable key={t} onPress={() => setTab(t)} style={{ height: 32, paddingHorizontal: 16, borderRadius: 7, alignItems: 'center', justifyContent: 'center', backgroundColor: tab === t ? c.card : 'transparent' }}>
            <Text style={{ fontSize: 12.5, fontWeight: tab === t ? '600' : '500', color: tab === t ? c.ink : c.ink3 }}>{t === 'outgoing' ? 'Outgoing' : 'Campaigns'}</Text>
          </Pressable>
        ))}
      </View>

      {tab === 'outgoing' ? (
        <View style={{ gap: 20 }}>
          {/* Master switch — turns ALL outbound calling on/off for this org. */}
          <Card style={{ padding: 0 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 16 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink }}>Outbound calling</Text>
                <Text style={{ fontSize: 11.5, color: c.ink3, marginTop: 2, lineHeight: 17 }}>
                  {obEnabled
                    ? 'On — SK Voice places outbound calls (reminders, offers) within the rules below. Inbound is unaffected.'
                    : 'Off — SK Voice places no outbound calls. Inbound calls still work normally.'}
                </Text>
              </View>
              <Chip kind={obEnabled ? 'solid' : undefined}>{obEnabled ? 'On' : 'Off'}</Chip>
              <Switch value={obEnabled} onValueChange={toggleOutbound} disabled={!canWrite || togglingOb} />
            </View>
          </Card>

          {/* Locked quiet-hours guardrail */}
          <Card style={{ padding: 0 }}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, padding: 16, borderBottomWidth: 1, borderBottomColor: c.line }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink }}>Quiet hours · regulatory guardrail</Text>
                <Text style={{ fontSize: 11.5, color: c.ink3, marginTop: 2, lineHeight: 17 }}>Tier-1 hard constraint, set by the destination country's telecom rules ({reg?.regulator ?? 'regulator'}). Locked — no call ever lands inside this window, and operators can't widen it.</Text>
              </View>
              <Chip kind="solid">🔒 Locked</Chip>
            </View>
            <View style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap', padding: 16 }}>
              <ReadField label="Quiet from" value={reg?.quietHoursStart ?? '—'} mono w={150} />
              <ReadField label="Quiet until" value={reg?.quietHoursEnd ?? '—'} mono w={150} />
              <ReadField label="Country" value={reg?.countryCode ?? '—'} w={150} />
              <ReadField label="Timezone" value={reg?.timezone ?? '—'} mono w={200} />
            </View>
          </Card>

          {/* cap + concurrency */}
          <View style={{ flexDirection: 'row', gap: 20, flexWrap: 'wrap' }}>
            <Card style={{ padding: 0, flex: 1, minWidth: 300 }}>
              <View style={{ padding: 14, borderBottomWidth: 1, borderBottomColor: c.line }}>
                <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink }}>Daily frequency cap</Text>
                <Text style={{ fontSize: 11.5, color: c.ink3, marginTop: 2 }}>Per customer, per day. You may lower it; the Setup ceiling caps the raise.</Text>
              </View>
              <View style={{ padding: 16 }}>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: 32, fontWeight: '600', color: c.ink }}>{cap || '0'}</Text>
                  <Text style={{ fontSize: 12, color: c.ink3 }}>/ ceiling {ceiling} calls</Text>
                </View>
                <NumInput value={cap} onChange={setCap} editable={canWrite} w={120} />
                <Text style={{ fontSize: 11, color: c.ink4, marginTop: 8 }}>Service-proximity reasons (carve-out) bypass this cap.</Text>
              </View>
            </Card>
            <Card style={{ padding: 0, flex: 1, minWidth: 300 }}>
              <View style={{ padding: 14, borderBottomWidth: 1, borderBottomColor: c.line }}>
                <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink }}>Per-org concurrency & lead time</Text>
                <Text style={{ fontSize: 11.5, color: c.ink3, marginTop: 2 }}>The gate's second level, tied to your Voice plan. Lead time applies when a reason has none.</Text>
              </View>
              <View style={{ flexDirection: 'row', gap: 16, padding: 16 }}>
                <LabeledNum label="Max concurrent calls" value={conc} onChange={setConc} editable={canWrite} />
                <LabeledNum label="Default lead (min)" value={lead} onChange={setLead} editable={canWrite} />
              </View>
            </Card>
          </View>

          {/* per-reason rules: class, window, lead, cap */}
          <Card style={{ padding: 0 }}>
            <View style={{ padding: 14, borderBottomWidth: 1, borderBottomColor: c.line }}>
              <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink }}>Preferred windows · per reason</Text>
              <Text style={{ fontSize: 11.5, color: c.ink3, marginTop: 2 }}>When a reason should prefer to call. Always inside quiet hours + the cap.</Text>
            </View>
            {winRows.length === 0
              ? <Text style={{ fontSize: 12, color: c.ink4, padding: 16 }}>No active reasons — add reasons in Setup first.</Text>
              : (
                <>
                  {/* header */}
                  <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.line, backgroundColor: c.paper2 }}>
                    <Text style={[colH(c), { flex: 1 }]}>Reason</Text>
                    <Text style={[colH(c), { width: 116 }]}>Class</Text>
                    <Text style={[colH(c), { width: 150 }]}>Window</Text>
                    <Text style={[colH(c), { width: 96 }]}>Lead</Text>
                    <Text style={[colH(c), { width: 84 }]}>Cap</Text>
                  </View>
                  {winRows.map((w, i) => (
                    <View key={w.reason_code} style={{ flexDirection: 'row', gap: 10, alignItems: 'center', paddingHorizontal: 16, paddingVertical: 11, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: c.line }}>
                      <Text style={{ flex: 1, fontFamily: theme.font.mono, fontSize: 12.5, color: c.ink2 }} numberOfLines={1}>{w.reason_code}</Text>
                      <View style={{ width: 116 }}><ClassChip cls={w.regulatory_class} /></View>
                      <View style={{ width: 150, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <TimeInput value={w.window_start} onChange={(v) => setWin(i, { window_start: v })} editable={canWrite} />
                        <Text style={{ fontSize: 12, color: c.ink4 }}>–</Text>
                        <TimeInput value={w.window_end} onChange={(v) => setWin(i, { window_end: v })} editable={canWrite} />
                      </View>
                      <View style={{ width: 96, flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                        <NumInput value={w.lead_minutes} onChange={(v) => setWin(i, { lead_minutes: v })} editable={canWrite} w={52} />
                        <Text style={{ fontSize: 11, color: c.ink4 }}>min</Text>
                      </View>
                      <View style={{ width: 84 }}><CapBadge carveOut={!!w.carve_out} /></View>
                    </View>
                  ))}
                </>
              )}
          </Card>

          {/* retry matrix */}
          <Card style={{ padding: 0 }}>
            <View style={{ padding: 14, borderBottomWidth: 1, borderBottomColor: c.line }}>
              <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink }}>Retry matrix · per outcome</Text>
              <Text style={{ fontSize: 11.5, color: c.ink3, marginTop: 2 }}>A null delay means terminal. Every retry re-runs the full policy engine, so it can never escape quiet hours or the cap.</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 12, paddingHorizontal: 18, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.line, backgroundColor: c.paper2 }}>
              <Text style={{ flex: 1.3, fontSize: 10, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', color: c.ink4 }}>Outcome</Text>
              <Text style={{ flex: 1.6, fontSize: 10, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', color: c.ink4 }}>Retry delay</Text>
              <Text style={{ width: 90, fontSize: 10, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', color: c.ink4 }}>Max retries</Text>
            </View>
            {RETRY_OUTCOMES.map(([code, label], i) => {
              const r = retryRows[code] ?? { retry_delay_minutes: '', max_retries: '0', terminal: true };
              return (
                <View key={code} style={{ flexDirection: 'row', gap: 12, alignItems: 'center', paddingHorizontal: 18, paddingVertical: 10, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: c.line }}>
                  <Text style={{ flex: 1.3, fontSize: 12.5, color: c.ink2 }}>{label}</Text>
                  <View style={{ flex: 1.6, flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                    {r.terminal
                      ? <Text style={{ flex: 1, fontFamily: theme.font.mono, fontSize: 12.5, color: c.ink4 }}>—</Text>
                      : <NumInput value={r.retry_delay_minutes} onChange={(v) => setRetry(code, { retry_delay_minutes: v })} editable={canWrite} w={72} />}
                    <Text style={{ fontSize: 11, color: c.ink4 }}>min</Text>
                    <Pressable disabled={!canWrite} onPress={() => setRetry(code, { terminal: !r.terminal })}>
                      <Chip kind={r.terminal ? 'solid' : 'default'}>{r.terminal ? 'terminal' : 'retry'}</Chip>
                    </Pressable>
                  </View>
                  <View style={{ width: 90 }}>
                    {r.terminal
                      ? <Text style={{ fontFamily: theme.font.mono, fontSize: 12.5, color: c.ink4 }}>0</Text>
                      : <NumInput value={r.max_retries} onChange={(v) => setRetry(code, { max_retries: v })} editable={canWrite} w={64} />}
                  </View>
                </View>
              );
            })}
          </Card>

          {canWrite && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ flex: 1, fontSize: 11, color: c.ink4 }}>All rules stay within the Setup-tier ceilings. Changes apply on the next policy-engine poll.</Text>
              <Button kind="primary" label={busy ? 'Saving…' : 'Save rules'} onPress={save} />
            </View>
          )}
        </View>
      ) : (
        // Campaigns sub-tab
        <Card style={{ padding: 0 }}>
          <View style={{ padding: 14, borderBottomWidth: 1, borderBottomColor: c.line }}>
            <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink }}>Campaign pacing & defaults</Text>
            <Text style={{ fontSize: 11.5, color: c.ink3, marginTop: 2 }}>Applies to bulk campaigns — on top of quiet hours, consent and the DND list. Never overrides a guardrail.</Text>
          </View>
          <View style={{ padding: 16, gap: 12 }}>
            <PacingRow label="Send rate (calls/min)" value={camp?.sendRatePerMin} />
            <PacingRow label="Max concurrent" value={camp?.maxConcurrent} />
            <PacingRow label="Daily send cap" value={camp?.dailySendCap} />
            <PacingRow label="Send window" value={camp ? `${camp.sendWindowStart}–${camp.sendWindowEnd}` : '—'} />
            <ToggleRow label="DND auto-dedupe at import" value={!!camp?.dndDedupeAtImport} />
            <ToggleRow label="Retry no-answers once" value={!!camp?.retryNoAnswerOnce} />
          </View>
        </Card>
      )}
    </ScreenBody>
  );
}

// Column-header text style for the reason table.
function colH(c: any) {
  return { fontSize: 10, fontWeight: '600' as const, letterSpacing: 0.5, textTransform: 'uppercase' as const, color: c.ink4 };
}

const AMBER = '#B8791F';

// Class chip: transactional = neutral, promotional = amber outline (per design).
function ClassChip({ cls }: { cls?: string }) {
  const { theme } = useTheme(); const c = theme.colors;
  const promo = cls === 'promotional';
  const col = promo ? AMBER : c.ink3;
  return (
    <View style={{ alignSelf: 'flex-start', borderWidth: 1, borderColor: promo ? AMBER : c.lineStrong, borderRadius: 6, paddingVertical: 3, paddingHorizontal: 8 }}>
      <Text style={{ fontSize: 11, fontFamily: theme.font.mono, color: col }}>{cls ?? '—'}</Text>
    </View>
  );
}

// Cap badge: carve-out reasons bypass the daily cap; others are capped.
function CapBadge({ carveOut }: { carveOut: boolean }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ alignSelf: 'flex-start', borderWidth: 1, borderColor: c.lineStrong, borderRadius: 6, paddingVertical: 3, paddingHorizontal: 8 }}>
      <Text style={{ fontSize: 11, color: c.ink3 }}>{carveOut ? 'carve-out' : 'capped'}</Text>
    </View>
  );
}

function ReadField({ label, value, mono, w }: { label: string; value: string; mono?: boolean; w: number }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ gap: 6, width: w }}>
      <Text style={{ fontSize: 12, fontWeight: '500', color: c.ink4 }}>{label}</Text>
      <View style={{ height: 36, justifyContent: 'center', paddingHorizontal: 12, borderWidth: 1, borderColor: c.line, borderRadius: 7, backgroundColor: c.paper2 }}>
        <Text style={{ fontFamily: mono ? theme.font.mono : theme.font.sans, fontSize: 13, color: c.ink2 }}>{value}</Text>
      </View>
    </View>
  );
}
function NumInput({ value, onChange, editable, w }: { value: string; onChange: (v: string) => void; editable: boolean; w: number }) {
  const { theme } = useTheme(); const c = theme.colors;
  return <TextInput value={value} onChangeText={onChange} editable={editable} keyboardType="numeric"
    style={{ width: w, height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: editable ? c.card : c.paper2, fontFamily: theme.font.mono, fontSize: 13 }} />;
}
function TimeInput({ value, onChange, editable }: { value: string; onChange: (v: string) => void; editable: boolean }) {
  const { theme } = useTheme(); const c = theme.colors;
  return <TextInput value={value} onChangeText={onChange} editable={editable} placeholder="HH:MM" placeholderTextColor={c.ink4}
    style={{ width: 64, height: 34, paddingHorizontal: 10, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: editable ? c.card : c.paper2, fontFamily: theme.font.mono, fontSize: 12.5, textAlign: 'center' }} />;
}
function LabeledNum({ label, value, onChange, editable }: { label: string; value: string; onChange: (v: string) => void; editable: boolean }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ gap: 6, flex: 1 }}>
      <Text style={{ fontSize: 12, fontWeight: '500', color: c.ink2 }}>{label}</Text>
      <TextInput value={value} onChangeText={onChange} editable={editable} keyboardType="numeric"
        style={{ height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: editable ? c.card : c.paper2, fontFamily: theme.font.mono, fontSize: 13 }} />
    </View>
  );
}
function PacingRow({ label, value }: { label: string; value: any }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
      <Text style={{ flex: 1, fontSize: 13, color: c.ink2 }}>{label}</Text>
      <Text style={{ fontFamily: theme.font.mono, fontSize: 13, color: c.ink }}>{value ?? '—'}</Text>
    </View>
  );
}
function ToggleRow({ label, value }: { label: string; value: boolean }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
      <Text style={{ flex: 1, fontSize: 13, color: c.ink2 }}>{label}</Text>
      <Switch value={value} disabled />
    </View>
  );
}
